import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { t as translate } from './translations';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

const toiletIcon = new L.DivIcon({
  className: 'custom-toilet-icon',
  html: `
    <div style="
      font-size: 32px;
      text-align: center;
      line-height: 32px;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
    ">💩</div>
  `,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32]
});

const userIcon = new L.DivIcon({
  className: 'custom-user-icon',
  html: `
    <div style="
      width: 16px;
      height: 16px;
      background: #3B82F6;
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(59, 130, 246, 0.5);
    "></div>
  `,
  iconSize: [22, 22],
  iconAnchor: [11, 11]
});

const filterToiletsByBounds = (toilets, bounds, maxMarkers = 150) => {
  if (!toilets || !bounds) return toilets?.slice(0, maxMarkers) || [];
  
  const filtered = toilets.filter(toilet => {
    if (!toilet.lat || !toilet.lng) return false;
    
    return (
      toilet.lat >= bounds.south &&
      toilet.lat <= bounds.north &&
      toilet.lng >= bounds.west &&
      toilet.lng <= bounds.east
    );
  });
  
  return filtered.slice(0, maxMarkers);
};

const MapSizeFix = () => {
  const map = useMap();
  
  useEffect(() => {
    const timers = [
      setTimeout(() => map.invalidateSize(), 100),
      setTimeout(() => map.invalidateSize(), 300),
      setTimeout(() => map.invalidateSize(), 500)
    ];
    
    return () => timers.forEach(timer => clearTimeout(timer));
  }, [map]);
  
  return null;
};

const BoundsChangeHandler = ({ onBoundsChange }) => {
  const map = useMap();
  
  useMapEvents({
    moveend: () => {
      if (onBoundsChange) {
        const bounds = map.getBounds();
        onBoundsChange({
          north: bounds.getNorth(),
          south: bounds.getSouth(),
          east: bounds.getEast(),
          west: bounds.getWest()
        });
      }
    }
  });
  
  return null;
};

const CenterChangeHandler = ({ onCenterChange }) => {
  const map = useMap();
  const lastCenterRef = useRef(null);
  
  useMapEvents({
    moveend: () => {
      if (onCenterChange) {
        const center = map.getCenter();
        const currentCenter = `${center.lat.toFixed(2)},${center.lng.toFixed(2)}`;
        
        // Nur feuern wenn sich Center deutlich geändert hat
        if (lastCenterRef.current !== currentCenter) {
          lastCenterRef.current = currentCenter;
          
          // Nur bei größeren Zoom-Levels (näher dran)
          const currentZoom = map.getZoom();
          
          if (currentZoom >= 12) { 
            onCenterChange(center.lat, center.lng);
          }
        }
      }
    }
  });
  
  return null;
};

const DynamicCenterZoom = ({ center, zoom }) => {
  const map = useMap();
  
  useEffect(() => {
    
    if (center && zoom) {
      map.setView(center, zoom, {
        animate: true,
        duration: 0.5
      });
    } else {
    }
  }, [center, zoom, map]);
  
  return null;
};

const AutoBounds = ({ toilets, userLocation, resetZoom, externalCenter, externalZoom }) => {
  const map = useMap();
  const hasZoomedRef = useRef(false);
  const lastCityRef = useRef(null);
  
  useEffect(() => {

    if (externalCenter && externalZoom) {
      return;
    }

    const currentCityKey = toilets?.[0]?.city || 'unknown';
    const cityChanged = lastCityRef.current !== currentCityKey;
    
    if (cityChanged) {
      hasZoomedRef.current = false;
      lastCityRef.current = currentCityKey;
    }
    
    if (hasZoomedRef.current && !cityChanged) {
      return;
    }
    
    if (toilets && toilets.length > 0) {
      
      const positions = toilets.map(t => {
        const lat = t.location?.lat || t.lat;
        const lng = t.location?.lng || t.lng;
        return [lat, lng];
      }).filter(pos => pos[0] && pos[1]);
      
      if (positions.length === 0) return;
      
      if (userLocation && !externalCenter) {
        positions.push([userLocation.lat, userLocation.lng]);
      }
      
      const bounds = L.latLngBounds(positions);
      
      map.fitBounds(bounds, { 
        padding: [80, 80], 
        maxZoom: 14,
        duration: 0.5
      });
      hasZoomedRef.current = true;
    } else if (userLocation && !hasZoomedRef.current && !externalCenter) {
      map.setView([userLocation.lat, userLocation.lng], 13);
      hasZoomedRef.current = true;
    }
  }, [toilets, userLocation, map, externalCenter, externalZoom]);
  
  return null;
};

const MapClickHandler = ({ onClick }) => {
  useMapEvents({
    click: (e) => {
      if (onClick) {
        const { lat, lng } = e.latlng;
        onClick(lat, lng);
      }
    }
  });
  
  return null;
};

const OSMAttribution = () => {
  const map = useMap();
  
  useEffect(() => {
    const attributionDiv = L.DomUtil.create('div', 'leaflet-control');
    attributionDiv.style.cssText = `
      position: absolute;
      bottom: 8px;
      left: 8px;
      background: rgba(255, 255, 255, 0.95);
      padding: 4px 8px;
      border-radius: 4px;
      font-size: 10px;
      box-shadow: 0 1px 5px rgba(0,0,0,0.2);
      border: 1px solid rgba(0,0,0,0.1);
      z-index: 1001;
    `;
    
    attributionDiv.innerHTML = `
      <a 
        href="https://www.openstreetmap.org/copyright" 
        target="_blank" 
        rel="noopener noreferrer"
        style="color: #2563eb; text-decoration: none; display: flex; align-items: center; gap: 2px; min-height: 24px;"
      >
        <span style="font-size: 9px;">©</span>
        <span style="font-weight: 500; white-space: nowrap;">OSM contributors</span>
      </a>
    `;
    
    const mapContainer = map.getContainer();
    mapContainer.appendChild(attributionDiv);
    
    return () => {
      if (mapContainer.contains(attributionDiv)) {
        mapContainer.removeChild(attributionDiv);
      }
    };
  }, [map]);
  
  return null;
};

const MapView = ({ 
  toilets, 
  allToilets, 
  onToiletClick, 
  userLocation, 
  onLocationRequest, 
  onClick,
  onBoundsChange,
  onCenterChange,
  maxToiletsDisplay,
  currentCity,
  center: externalCenter,    
  zoom: externalZoom,
  southPoleMode = false,
  onToggleSouthPole,
  isAprilFools = false,
  lang = 'de',
  infoBadgeOf,
  infoBadgeShown,
  infoBadgeMove,
}) => {
  const mapRef = useRef(null);
  const [currentBounds, setCurrentBounds] = useState(null);
  
  const defaultCenter = [51.1657, 10.4515];
  const defaultZoom = 6;

  const toiletsForCenter = allToilets && allToilets.length > 0 ? allToilets : toilets;

  const center = externalCenter 
    ? externalCenter 
    : toiletsForCenter && toiletsForCenter.length > 0 
      ? [toiletsForCenter[0].lat, toiletsForCenter[0].lng]
      : userLocation 
        ? [userLocation.lat, userLocation.lng]
        : defaultCenter;
  
const zoom = externalZoom 
    ? externalZoom 
    : toiletsForCenter && toiletsForCenter.length > 0 ? 13 : userLocation ? 13 : defaultZoom;
  
  const visibleToilets = filterToiletsByBounds(toilets, currentBounds, 150);
  
  return (
    <div className="w-full h-full relative">

      {isAprilFools && (
        <div className="absolute top-20 left-3 z-[1002]">
          <button
            onClick={onToggleSouthPole}
            className={`px-3 py-2 rounded-lg text-xs font-bold shadow-lg transition-all ${
              southPoleMode
                ? 'bg-cyan-500 text-white'
                : 'bg-cyan-50 text-cyan-700 border border-cyan-300 hover:bg-cyan-100'
            }`}
          >
            {southPoleMode 
               ? translate('aprilFools.southPoleOn', lang)
               : translate('aprilFools.southPoleOff', lang)
            }
          </button>
        </div>
      )}

      <div
        style={{
          transform: southPoleMode ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.8s ease-in-out',
          height: '100%',
          width: '100%',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }}
      >
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        ref={mapRef}
        zoomControl={true}
        whenReady={() => {
          if (mapRef.current) {
            setTimeout(() => {
              mapRef.current.invalidateSize();
            }, 200);
          }
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapSizeFix />

        {externalCenter && externalZoom && (
          <DynamicCenterZoom center={externalCenter} zoom={externalZoom} />
        )}

        <AutoBounds 
          toilets={allToilets || toilets} 
          userLocation={userLocation}
          externalCenter={externalCenter}
          externalZoom={externalZoom}
        />

        {onBoundsChange && <BoundsChangeHandler onBoundsChange={onBoundsChange} />}

        <BoundsChangeHandler onBoundsChange={setCurrentBounds} />

        {onCenterChange && <CenterChangeHandler onCenterChange={onCenterChange} />}

        <OSMAttribution />

        {onClick && <MapClickHandler onClick={onClick} />}

        {visibleToilets && visibleToilets.map((toilet) => (
          <Marker
            key={toilet.id}
            position={[toilet.lat, toilet.lng]}
            icon={toiletIcon}
            eventHandlers={{
              click: () => {
                if (onToiletClick) {
                  onToiletClick(toilet);
                }
              }
            }}
          />
        ))}

        {userLocation && (
          <Marker
            position={[userLocation.lat, userLocation.lng]}
            icon={userIcon}
          >
            <Popup>
              <div className="p-2 min-w-[150px]">
                <p className="font-semibold text-blue-600 mb-1">📍 Dein Standort</p>
                {userLocation.accuracy && (
                  <p className="text-xs text-gray-500">
                    Genauigkeit: ±{Math.round(userLocation.accuracy)}m
                  </p>
                )}
              </div>
            </Popup>
          </Marker>
        )}
      </MapContainer>
      </div> 

      {onLocationRequest && (
        <button
          onClick={onLocationRequest}
          className="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg hover:bg-gray-50 z-[1000] flex items-center gap-2"
          title="Mein Standort"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 2L12 6M12 18L12 22M22 12L18 12M6 12L2 12" />
            <circle cx="12" cy="12" r="4" />
          </svg>
          {!userLocation && <span className="text-sm font-medium">Standort</span>}
        </button>
      )}

      {allToilets && (allToilets.length > maxToiletsDisplay || currentCity) && (
        <div className="absolute bottom-16 left-4 bg-blue-600 text-white px-3 py-2 rounded-lg shadow-lg z-[1000] text-sm">
          <>
            {currentCity && (
              <div className="font-bold mb-1 flex items-center gap-2">
                📍 {currentCity}
              </div>
            )}
            {allToilets.length > maxToiletsDisplay && (
              <div className="flex items-center gap-2">
                <span>ℹ️</span>
                <span>
                  {toilets.length} {infoBadgeOf} {allToilets.length} {infoBadgeShown}
                  <span className="block text-xs opacity-90 mt-0.5">
                    {infoBadgeMove}
                  </span>
                </span>
              </div>
            )}
          </>
        </div>
      )}
    </div>
  );
};

export default MapView;